<div hlmCard>
  @if (loading()) {
    <div class="space-y-4 p-4" aria-busy="true">
      @for (row of skeletonRows; track row) {
        <div class="flex items-center gap-4">
          <hlm-skeleton class="h-4 w-28 shrink-0" />
          <hlm-skeleton class="h-4 w-24 shrink-0" />
          <hlm-skeleton class="hidden h-4 w-20 shrink-0 md:block" />
          <hlm-skeleton class="hidden h-4 min-w-0 flex-1 md:block" />
          <hlm-skeleton class="ms-auto h-6 w-20 shrink-0 rounded-full" />
        </div>
      }
    </div>
  } @else if (filteredRecords().length === 0) {
    <hlm-empty class="py-10">
      <div hlmEmptyHeader>
        <div hlmEmptyMedia variant="icon"><ng-icon name="lucideReceipt" /></div>
        <p hlmEmptyTitle>Keine Einlösungen</p>
        <p hlmEmptyDescription>{{ emptyMessage() }}</p>
      </div>
    </hlm-empty>
  } @else {
    <div hlmTableContainer>
      <table hlmTable>
        <thead hlmTHead>
          <tr hlmTr>
            <th hlmTh>
              <button
                hlmBtn
                variant="ghost"
                size="xs"
                class="-ms-2"
                (click)="handleSort('redeemed_at')">
                Datum
                <ng-icon [name]="sortIcon('redeemed_at')" />
              </button>
            </th>
            <th hlmTh>
              <button
                hlmBtn
                variant="ghost"
                size="xs"
                class="-ms-2"
                (click)="handleSort('voucher_code')">
                Gutschein
                <ng-icon [name]="sortIcon('voucher_code')" />
              </button>
            </th>
            @if (showMemberColumn()) {
              <th hlmTh>
                <button
                  hlmBtn
                  variant="ghost"
                  size="xs"
                  class="-ms-2"
                  (click)="handleSort('member_company_name')">
                  Mitglied
                  <ng-icon [name]="sortIcon('member_company_name')" />
                </button>
              </th>
            }
            <th hlmTh class="text-right">Guthaben vorher</th>
            <th hlmTh class="text-right">Guthaben nachher</th>
            <th hlmTh class="text-right">
              <button hlmBtn variant="ghost" size="xs" class="-me-2" (click)="handleSort('amount')">
                Betrag
                <ng-icon [name]="sortIcon('amount')" />
              </button>
            </th>
            @if (showPayout()) {
              <th hlmTh class="text-right">
                <button
                  hlmBtn
                  variant="ghost"
                  size="xs"
                  class="-me-2"
                  (click)="handleSort('payout_cents')">
                  Auszahlung
                  <ng-icon [name]="sortIcon('payout_cents')" />
                </button>
              </th>
            }
            @if (showStatusColumn()) {
              <th hlmTh>
                <button
                  hlmBtn
                  variant="ghost"
                  size="xs"
                  class="-ms-2"
                  (click)="handleSort('settlement')">
                  Abrechnung
                  <ng-icon [name]="sortIcon('settlement')" />
                </button>
              </th>
            }
          </tr>
        </thead>

        <tbody hlmTBody>
          @for (view of pagedRecords(); track view.row.redemption_id) {
            <tr hlmTr class="cursor-pointer" (click)="openVoucherModal(view.row.voucher_code)">
              <td hlmTd class="whitespace-nowrap">{{ view.date }}</td>

              <td hlmTd>
                <div class="flex items-center gap-2">
                  <span class="font-mono font-medium">{{ view.row.voucher_code }}</span>
                  @if (view.row.is_internal) {
                    <span hlmBadge variant="outline">intern</span>
                  }
                  @if (view.row.voucher_product_name) {
                    <span hlmBadge variant="secondary" class="max-w-40">
                      <span class="truncate">{{ view.row.voucher_product_name }}</span>
                    </span>
                  }
                </div>
              </td>

              @if (showMemberColumn()) {
                <td hlmTd class="text-muted-foreground">
                  <span
                    class="block max-w-48 truncate"
                    [hlmTooltip]="view.row.member_company_name ?? ''"
                    [tooltipDisabled]="!view.row.member_company_name">
                    {{ view.row.member_company_name || '–' }}
                  </span>
                </td>
              }

              <td hlmTd class="text-muted-foreground text-right tabular-nums">
                {{ view.balanceBefore }}
              </td>
              <td hlmTd class="text-muted-foreground text-right tabular-nums">
                {{ view.balanceAfter }}
              </td>
              <td hlmTd class="text-right font-medium tabular-nums">{{ view.amount }} €</td>

              @if (showPayout()) {
                <td hlmTd class="text-right tabular-nums">
                  @if (view.payout) {
                    <span
                      [hlmTooltip]="view.deductionTooltip"
                      [tooltipDisabled]="!view.deductionTooltip">
                      {{ view.payout }}
                    </span>
                  } @else {
                    <span class="text-muted-foreground">–</span>
                  }
                </td>
              }

              @if (showStatusColumn()) {
                <td hlmTd>
                  <span hlmBadge [variant]="view.statusVariant">{{ view.statusLabel }}</span>
                </td>
              }
            </tr>
          }
        </tbody>
      </table>
    </div>

    @if (totalPages() > 1) {
      <div hlmCardFooter class="flex-col gap-3 border-t pt-3 sm:flex-row sm:justify-between">
        <p class="text-muted-foreground text-xs">
          Seite {{ currentPage() }} von {{ totalPages() }} · {{ filteredRecords().length }} Einträge
        </p>
        <app-table-pagination
          class="w-auto"
          [page]="currentPage()"
          [totalPages]="totalPages()"
          (pageChange)="handlePageChange($event)" />
      </div>
    }
  }
</div>

<hlm-dialog [state]="showVoucherModal() ? 'open' : 'closed'" (stateChanged)="onDialogState($event)">
  <hlm-dialog-content
    *hlmDialogPortal
    class="max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto sm:max-w-2xl">
    <div hlmDialogHeader>
      <h3 hlmDialogTitle>Gutschein-Details</h3>
    </div>
    @if (selectedVoucherCode()) {
      <app-voucher-details [voucherCode]="selectedVoucherCode()!" />
    }
  </hlm-dialog-content>
</hlm-dialog>
